import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import firebase from '../Firebase';

import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import HealingIcon from '@mui/icons-material/Healing';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import { ProfileContext } from '../contexts/Profile';

// Main component
const MedicalRecordFeed = () => {
  const { currentProfile } = useContext(ProfileContext);
  const [patientData, setPatientData] = useState(null);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentProfile) return;
    const fetchData = async () => {
      try {
        firebase
          .firestore()
          .collectionGroup('medicalRecords')
          .where('profileId', '==', currentProfile.id)
          .onSnapshot((snapshot) => {
            console.log('Snapshot:', snapshot.docs, currentProfile.id);
            const mrecords = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            // records is [{data: []}]
            const allDataObjects = mrecords.map((record) => record.data);
            const data = allDataObjects.reduce((acc, curr) => {
              return acc.concat(curr);
            }, []);
            console.log('All Data:', data);
            // Process all medical records and sort them by date
            const records = [];

            // Process allergies
            const allergies = data
              .filter(
                (item) =>
                  item.resource &&
                  item.resource.resourceType === 'AllergyIntolerance',
              )
              .map((item) => ({
                ...item.resource,
                type: 'allergy',
                date: item.resource.recordedDate,
                icon: 'error',
                title: 'Allergy',
                subTitle: `${item.resource.code?.text || 'Unknown'}`,
                criticality: item.resource.criticality,
              }));
            records.push(...allergies);

            // Process conditions
            const conditions = data
              .filter(
                (item) =>
                  item.resource && item.resource.resourceType === 'Condition',
              )
              .map((item) => ({
                ...item.resource,
                type: 'condition',
                date: item.resource.recordedDate,
                icon: 'healing',
                title: 'Condition',
                subTitle: `${item.resource.code?.text || 'Unknown'}`,
              }));
            records.push(...conditions);

            // Process diagnostic reports
            const diagnosticReports = data
              .filter(
                (item) =>
                  item.resource &&
                  item.resource.resourceType === 'DiagnosticReport',
              )
              .map((item) => ({
                ...item.resource,
                type: 'diagnosticReport',
                date: item.resource.effectiveDateTime || item.resource.issued,
                icon: 'assignment',
                title: 'Report',
                subTitle: `${item.resource.code?.text || 'Unknown'}`,
              }));
            records.push(...diagnosticReports);

            // Process immunizations
            const immunizations = data
              .filter(
                (item) =>
                  item.resource &&
                  item.resource.resourceType === 'Immunization',
              )
              .map((item) => ({
                ...item.resource,
                type: 'immunization',
                date: item.resource.occurrenceDateTime,
                icon: 'vaccines',
                title: `Immunization: ${
                  item.resource.vaccineCode?.text || 'Unknown'
                }`,
              }));
            records.push(...immunizations);

            // Process observations
            const observations = data
              .filter(
                (item) =>
                  item.resource && item.resource.resourceType === 'Observation',
              )
              .map((item) => ({
                ...item.resource,
                type: 'observation',
                date: item.resource.issued,
                icon: 'smoking',
                title: `Observation: ${item.resource.code?.text || 'Unknown'}`,
              }));
            records.push(...observations);

            // Process procedures
            const procedures = data
              .filter(
                (item) =>
                  item.resource && item.resource.resourceType === 'Procedure',
              )
              .map((item) => ({
                ...item.resource,
                type: 'procedure',
                date: item.resource.performedDateTime,
                icon: 'medical',
                title: `Procedure: ${item.resource.code?.text || 'Unknown'}`,
              }));
            records.push(...procedures);

            // Process encounters
            const encounters = data
              .filter(
                (item) =>
                  item.resource && item.resource.resourceType === 'Encounter',
              )
              .map((item) => ({
                ...item.resource,
                type: 'encounter',
                date: item.resource.period?.start,
                icon: 'hospital',
                title: `Visit: ${item.resource.type?.[0]?.text || 'Unknown'}`,
              }));
            records.push(...encounters);

            // Process medications
            const medicationAdmins = data
              .filter(
                (item) =>
                  item.resource &&
                  item.resource.resourceType === 'MedicationAdministration',
              )
              .map((item) => ({
                ...item.resource,
                type: 'medication',
                date: item.resource.effectiveDateTime,
                icon: 'medication',
                title: 'Medication Administration',
              }));
            records.push(...medicationAdmins);

            // Process document references
            const documents = data
              .filter(
                (item) =>
                  item.resource &&
                  item.resource.resourceType === 'DocumentReference',
              )
              .map((item) => ({
                ...item.resource,
                type: 'document',
                date: item.resource.date,
                icon: 'description',
                title: `Document: ${item.resource.type?.text || 'Unknown'}`,
              }));
            records.push(...documents);

            // Sort all records by date (newest first)
            const sortedRecords = records
              .filter((record) => record.date) // Only include records with dates
              .sort((a, b) => {
                const dateA = new Date(a.date).getTime();
                const dateB = new Date(b.date).getTime();
                return dateB - dateA; // Descending order
              });

            setMedicalRecords(sortedRecords);
            setLoading(false);
          });
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [currentProfile]);

  // Format date to display in a readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get patient age based on birthdate
  const getAge = (birthDate) => {
    if (!birthDate) return 'Unknown';

    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  };

  // Get the icon component based on record type
  const getIconComponent = (iconType) => {
    switch (iconType) {
      case 'error':
        return <ErrorIcon />;
      case 'healing':
        return <HealingIcon />;
      case 'assignment':
        return <AssignmentIcon />;
      case 'vaccines':
        return <VaccinesOutlinedIcon />;
      case 'smoking':
        return <SmokingRoomsIcon />;
      case 'medical':
        return <MedicalServicesIcon />;
      case 'hospital':
        return <LocalHospitalIcon />;
      case 'medication':
        return <MedicationIcon />;
      case 'description':
        return <DescriptionIcon />;
      default:
        return <DescriptionIcon />;
    }
  };

  const getIconColor = (iconType) => {
    switch (iconType) {
      case 'error':
        return 'error.main';
      case 'healing':
        return 'warning.main';
      case 'assignment':
        return 'info.main';
      case 'vaccines':
        return 'success.main';
      case 'smoking':
        return 'secondary.main';
      case 'medical':
        return 'info.dark';
      case 'hospital':
        return 'primary.main';
      case 'medication':
        return 'error.light';
      case 'description':
        return 'text.secondary';
      default:
        return 'text.secondary';
    }
  };

  // Render the details of each medical record based on its type
  const renderCardDetails = (record) => {
    switch (record.type) {
      case 'allergy':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Clinical Status:{' '}
              {record.clinicalStatus?.coding?.[0]?.display || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Criticality:
              </Typography>
              <Chip
                size="small"
                color={record.criticality === 'high' ? 'error' : 'warning'}
                label={record.criticality || 'Unknown'}
                sx={{ ml: 1 }}
              />
            </Box>
            {record.reaction && record.reaction.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Reaction:{' '}
                {record.reaction[0].description ||
                  record.reaction[0].manifestation?.[0]?.text ||
                  'Unknown'}
              </Typography>
            )}
            {record.onsetPeriod?.start && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Onset: {formatDate(record.onsetPeriod.start)}
              </Typography>
            )}
          </Box>
        );

      case 'condition':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Clinical Status:{' '}
              {record.clinicalStatus?.coding?.[0]?.display || 'Unknown'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              Severity: {record.severity?.text || 'Unknown'}
            </Typography>
            {record.onsetPeriod?.start && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Onset: {formatDate(record.onsetPeriod.start)}
              </Typography>
            )}
          </Box>
        );

      case 'diagnosticReport':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
            {record.conclusionCode && record.conclusionCode.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Conclusion: {record.conclusionCode[0].text || 'Unknown'}
              </Typography>
            )}
            {record.performer && record.performer.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Performed by: {record.performer[0].display || 'Unknown'}
              </Typography>
            )}
          </Box>
        );

      case 'immunization':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
            {record.manufacturer && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Manufacturer: {record.manufacturer.display || 'Unknown'}
              </Typography>
            )}
            {record.lotNumber && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Lot Number: {record.lotNumber}
              </Typography>
            )}
            {record.doseQuantity && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Dose: {record.doseQuantity.value} {record.doseQuantity.unit}
              </Typography>
            )}
          </Box>
        );

      case 'observation':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
            {record.valueCodeableConcept && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Value:{' '}
                {record.valueCodeableConcept.text ||
                  record.valueCodeableConcept.coding?.[0]?.display ||
                  'Unknown'}
              </Typography>
            )}
            {record.performer && record.performer.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Performer: {record.performer[0].display || 'Unknown'}
              </Typography>
            )}
          </Box>
        );

      case 'procedure':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
            {record.reasonCode && record.reasonCode.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Reason: {record.reasonCode[0].text || 'Unknown'}
              </Typography>
            )}
            {record.performer && record.performer.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Performer: {record.performer[0].actor?.display || 'Unknown'}
              </Typography>
            )}
          </Box>
        );

      case 'encounter':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
            {record.class && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Class: {record.class.display || 'Unknown'}
              </Typography>
            )}
            {record.reasonCode && record.reasonCode.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Reason: {record.reasonCode[0].text || 'Unknown'}
              </Typography>
            )}
            {record.location && record.location.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Location: {record.location[0].location?.display || 'Unknown'}
              </Typography>
            )}
          </Box>
        );

      case 'medication':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
          </Box>
        );

      case 'document':
        return (
          <Box sx={{ textAlign: 'start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Status: {record.status || 'Unknown'}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              Doc Status: {record.docStatus || 'Unknown'}
            </Typography>
            {record.author && record.author.length > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                Author: {record.author[0].display || 'Unknown'}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No additional details available
          </Typography>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading medical records...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: 'error.main' }}>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 8 }}>
      {/* Patient Information Card */}
      {/* {patientData && (
        <Container
          maxWidth="sm"
          sx={{ mb: 3, textAlign: 'start' }}
          disableGutters
        >
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor:
                      patientData.gender === 'male'
                        ? 'primary.main'
                        : 'secondary.main',
                    width: 56,
                    height: 56,
                    mr: 2,
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {patientData.name?.[0]?.given?.[0] || ''}{' '}
                    {patientData.name?.[0]?.family || ''}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {patientData.gender || 'Unknown'} •{' '}
                    {getAge(patientData.birthDate)} years
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    DOB: {formatDate(patientData.birthDate)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Contact
                  </Typography>
                  {patientData.telecom && patientData.telecom.length > 0 ? (
                    <Typography variant="body2">
                      {patientData.telecom[0].value || 'None provided'}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      No contact information
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Address
                  </Typography>
                  {patientData.address && patientData.address.length > 0 ? (
                    <Typography variant="body2">
                      {patientData.address[0].line?.[0] || ''},{' '}
                      {patientData.address[0].city || ''},{' '}
                      {patientData.address[0].state || ''}{' '}
                      {patientData.address[0].postalCode || ''}
                    </Typography>
                  ) : (
                    <Typography variant="body2">No address provided</Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      )} */}

      {/* Medical Record Timeline */}
      <Container maxWidth="sm" disableGutters>
        {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Medical Timeline
        </Typography> */}

        {medicalRecords.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No medical records found</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {medicalRecords.map((record, index) => (
              <Card key={index} elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ flex: 1, textAlign: 'start' }}>
                    <Stack direction={'column'} gap={3} sx={{ width: '100%' }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ width: '100%' }}
                      >
                        <Avatar
                          sx={{
                            mr: 2,
                            bgcolor: `${getIconColor(record.icon)}`,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {getIconComponent(record.icon)}
                        </Avatar>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'medium', flex: 1 }}
                        >
                          {record.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            mb: 1,
                          }}
                        >
                          {formatDate(record.date)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        mt: 1,
                      }}
                    >
                      {record.subTitle && (
                        <Box sx={{ fontWeight: 'medium' }}>
                          {record.subTitle}
                        </Box>
                      )}
                      <Box sx={{ mt: 1 }}>{renderCardDetails(record)}</Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MedicalRecordFeed;
